import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import {
  DEFAULT_TARGET_ROAS,
  targetRoasToEquivalentStrategy,
} from 'src/modals/SponsoredListings/sponsoredListings.util';
import { BoostIcon } from 'src/svgs/BoostIcon';
import { BoostIconLight } from 'src/svgs/BoostIconLight';
import { BoostStrategyIcon } from 'src/svgs/BoostStrategyIcon';
import { PosIconProps } from 'src/svgs/SvgWrapper';
import { BiddingMode, SpendStrategy } from 'src/WebApiController';

function spendStrategyToLevel(
  strategy: SpendStrategy | null | undefined
): 1 | 2 | 3 | 4 | 5 | 6 {
  switch (strategy) {
    case SpendStrategy.HoldForBetterPrices:
      return 2;
    case SpendStrategy.SteadyHarvest:
      return 3;
    case SpendStrategy.MeasuredPush:
      return 4;
    case SpendStrategy.Accelerate:
      return 5;
    case SpendStrategy.TurboBoost:
      return 6;
    default:
      return 1;
  }
}

export const BoostIconDisplay = (
  props: Omit<PosIconProps, 'viewBox' | 'fill' | 'stroke'> & {
    isSelected?: boolean;
    inactive?: boolean;
    spendStrategy?: SpendStrategy | null;
    targetRoas?: number | null;
    biddingMode?: BiddingMode | null;
  }
) => {
  const { isDarkMode } = useSiteTheme();

  // Resolve the effective strategy:
  // 1. Explicit spendStrategy (for Automatic campaigns)
  // 2. Derive from targetRoas via fee-cap equivalence (for TargetRoas campaigns)
  // 3. If biddingMode is TargetRoas but targetRoas is null (API didn't return it),
  //    fall back to the default ROAS value so the icon still shows a tier.
  let effectiveStrategy: SpendStrategy | undefined;
  if (props.spendStrategy != null) {
    effectiveStrategy = props.spendStrategy;
  } else if (props.targetRoas != null) {
    effectiveStrategy = targetRoasToEquivalentStrategy(props.targetRoas);
  } else if (props.biddingMode === BiddingMode.TargetRoas) {
    effectiveStrategy = targetRoasToEquivalentStrategy(DEFAULT_TARGET_ROAS);
  }

  if (effectiveStrategy != null && !props.inactive) {
    const level = spendStrategyToLevel(effectiveStrategy);
    return <BoostStrategyIcon {...props} level={level} />;
  }

  return props.isSelected ? (
    <BoostIconLight
      {...props}
      inactive={props.inactive}
      activeFill={
        isDarkMode ? vars.color.surfacePrimary : vars.color.iconPrimaryInverted
      }
    />
  ) : (
    <BoostIcon {...props} inactive={props.inactive} />
  );
};
